import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Grid, MenuItem, Autocomplete, CircularProgress, Alert, Tabs, Tab, TableSortLabel,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Visibility from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { projectsAPI, clientsAPI, configAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const STATUS_COLORS = {
  opportunity: 'default', conceptual: 'info', offer_preparation: 'warning',
  offer_sent: 'secondary', offer_approved: 'success', in_progress: 'primary',
  commissioning: 'warning', completed: 'success', cancelled: 'error',
};

const STATUS_LABELS = {
  opportunity: 'Oportunidad', conceptual: 'Ing. Conceptual',
  offer_preparation: 'Preparando Oferta', offer_sent: 'Oferta Enviada',
  offer_approved: 'Oferta Aprobada', in_progress: 'Ing. Detalle',
  commissioning: 'Ejecucion', completed: 'Completado', cancelled: 'Cancelado',
};

// Statuses considered "approved" (oferta aprobada o posterior)
const APPROVED_STATUSES = ['offer_approved', 'in_progress', 'commissioning', 'completed'];

export default function Projects() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [docSettings, setDocSettings] = useState({
    documents_base_path: '/app/documents',
    project_path_template: '{continent}/{year}/{client}/{offer_code} {project_name}',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [listTab, setListTab] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [projSortField, setProjSortField] = useState('code');
  const [projSortDir, setProjSortDir] = useState('asc');

  const handleProjSort = (field) => {
    if (projSortField === field) {
      setProjSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setProjSortField(field);
      setProjSortDir('asc');
    }
  };

  const sortProjectList = (list) => [...list].sort((a, b) => {
    const va = a[projSortField] ?? '';
    const vb = b[projSortField] ?? '';
    const cmp = String(va).localeCompare(String(vb), 'es', { numeric: true });
    return projSortDir === 'desc' ? -cmp : cmp;
  });
  const [form, setForm] = useState({ name: '' });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [projectsResp, clientsResp, ptResp, settingsResp] = await Promise.all([
          projectsAPI.list(), clientsAPI.list(), configAPI.listProjectTypes(), configAPI.getSettings(),
        ]);
        setProjects(projectsResp.data);
        setClients(clientsResp.data);
        setProjectTypes(ptResp.data);
        if (settingsResp.data) setDocSettings(settingsResp.data);
        setError(null);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Error al cargar los datos.');
      } finally { setLoading(false); }
    };
    loadData();
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const generateOfferCode = () => {
    const year = new Date().getFullYear();
    const yearProjects = projects.filter(p => p.offer_code?.includes(String(year)));
    return `OF-${year}-${String(yearProjects.length + 1).padStart(3, '0')}`;
  };

  const resolveTemplate = (template, context) => {
    let path = template;
    Object.entries(context).forEach(([key, val]) => {
      path = path.replace(new RegExp(`\\{${key}\\}`, 'g'), val || '');
    });
    return path.replace(/\/+/g, '/').replace(/^\/|\/$/g, '');
  };

  const handleCreate = async () => {
    if (!selectedClient || !form.name) return;
    try {
      const offerCode = generateOfferCode();
      const year = new Date().getFullYear();
      const clientName = selectedClient.name.split(' ')[0].toUpperCase();

      await projectsAPI.create({
        name: form.name,
        project_type: 'liquid_process',
        client_id: selectedClient.id,
        code: null,
        offer_code: offerCode,
        request_date: new Date().toISOString(),
        documents_path: `PROYECTOS/${selectedClient.continent}/${clientName}/${year}/${offerCode} ${form.name}`,
      });

      const updatedResp = await projectsAPI.list();
      setProjects(updatedResp.data);
      setOpen(false);
      setSelectedClient(null);
      setForm({ name: '' });
      setError(null);
    } catch (err) {
      console.error('Error creating project:', err);
      setError('Error al crear el proyecto.');
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Desconocido';
  };

  const handleDeleteProject = async () => {
    if (!deleteConfirm) return;
    try {
      await projectsAPI.delete(deleteConfirm.id);
      setProjects(projects.filter(p => p.id !== deleteConfirm.id));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting project:', err);
      setError('Error al eliminar el proyecto.');
      setDeleteConfirm(null);
    }
  };

  // Split projects into approved and not-approved
  const approvedProjects = projects.filter(p => APPROVED_STATUSES.includes((p.status || '').toLowerCase()));
  const notApprovedProjects = projects.filter(p => !APPROVED_STATUSES.includes((p.status || '').toLowerCase()) && (p.status || '').toLowerCase() !== 'cancelled');
  const cancelledProjects = projects.filter(p => (p.status || '').toLowerCase() === 'cancelled');

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}><CircularProgress /></Box>;
  }

  const renderProjectTable = (projectList, emptyMsg, showDelete = false) => {
    const sorted = sortProjectList(projectList);
    const projCols = [
      ['code', 'N. Proyecto'], ['offer_code', 'N. Oferta'], ['name', 'Nombre'],
      ['project_type', 'Tipo'], ['client_id', 'Cliente'], ['status', 'Estado'],
      ['request_date', 'Peticion'], ['delivery_weeks', 'Plazo (sem)'],
    ];
    return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#c62828' }}>
            {projCols.map(([field, label]) => (
              <TableCell key={field} sx={{ fontWeight: 'bold', color: '#fff' }} sortDirection={projSortField === field ? projSortDir : false}>
                <TableSortLabel active={projSortField === field} direction={projSortField === field ? projSortDir : 'asc'} onClick={() => handleProjSort(field)}>{label}</TableSortLabel>
              </TableCell>
            ))}
            <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.length === 0 ? (
            <TableRow><TableCell colSpan={9} align="center">{emptyMsg}</TableCell></TableRow>
          ) : (
            sorted.map((p) => (
              <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/projects/${p.id}`)}>
                <TableCell><strong>{p.code}</strong></TableCell>
                <TableCell>{p.offer_code || '-'}</TableCell>
                <TableCell>{p.name}</TableCell>
                <TableCell><Chip label={projectTypes.find(t => t.value === p.project_type)?.label || p.project_type} size="small" variant="outlined" /></TableCell>
                <TableCell>{getClientName(p.client_id)}</TableCell>
                <TableCell><Chip label={STATUS_LABELS[(p.status || '').toLowerCase()] || p.status} color={STATUS_COLORS[(p.status || '').toLowerCase()] || 'default'} size="small" /></TableCell>
                <TableCell>{p.request_date ? new Date(p.request_date).toISOString().slice(0, 10) : '-'}</TableCell>
                <TableCell>{p.delivery_weeks || '-'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Ver proyecto"><IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/projects/${p.id}`); }}><Visibility fontSize="small" /></IconButton></Tooltip>
                  {showDelete && isAdmin && (
                    <Tooltip title="Eliminar oferta"><IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(p); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
    );
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" fontWeight="bold">Proyectos</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>Nueva Peticion</Button>
      </Box>

      {/* Tabs to switch between lists */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={listTab} onChange={(e, v) => setListTab(v)}>
          <Tab label={`Proyectos aprobados (${approvedProjects.length})`} />
          <Tab label={`Peticiones / En oferta (${notApprovedProjects.length})`} />
          {cancelledProjects.length > 0 && <Tab label={`Cancelados (${cancelledProjects.length})`} />}
        </Tabs>
      </Paper>

      {listTab === 0 && (
        <Box>
          <Typography variant="subtitle1" color="textSecondary" gutterBottom>
            Proyectos con oferta aprobada o en ejecucion
          </Typography>
          {renderProjectTable(approvedProjects, 'No hay proyectos aprobados')}
        </Box>
      )}

      {listTab === 1 && (
        <Box>
          <Typography variant="subtitle1" color="textSecondary" gutterBottom>
            Peticiones y ofertas en curso (aun no aprobadas)
          </Typography>
          {renderProjectTable(notApprovedProjects, 'No hay peticiones/ofertas pendientes', true)}
        </Box>
      )}

      {listTab === 2 && cancelledProjects.length > 0 && (
        <Box>
          <Typography variant="subtitle1" color="textSecondary" gutterBottom>
            Proyectos cancelados
          </Typography>
          {renderProjectTable(cancelledProjects, 'No hay proyectos cancelados')}
        </Box>
      )}

      {/* Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nueva Peticion de Proyecto</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Registra una nueva peticion de un cliente. El resto de datos se introduciran dentro del proyecto.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={clients}
                getOptionLabel={(option) => `${option.code || option.id} - ${option.name}`}
                value={selectedClient}
                onChange={(e, val) => setSelectedClient(val)}
                renderInput={(params) => <TextField {...params} label="Cliente" required />}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
              />
            </Grid>
            <Grid item xs={12}><TextField fullWidth label="Nombre del Proyecto" name="name" value={form.name} onChange={handleChange} required /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpen(false); setSelectedClient(null); }}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!selectedClient || !form.name}>Crear Proyecto</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo confirmación eliminar proyecto */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>⚠️ Eliminar proyecto / oferta</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Vas a eliminar permanentemente el proyecto:
          </Typography>
          <Typography variant="h6" color="error" gutterBottom>
            {deleteConfirm?.offer_code || '-'} — {deleteConfirm?.name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Esta acción no se puede deshacer. Se eliminarán todos los datos asociados al proyecto.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} color="inherit">Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDeleteProject}>
            Eliminar definitivamente
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
